import axios from 'axios';
import { httpWrapper } from 'services/http';
import userService from 'services/user';
import { endpoint } from '.';
import { API_URL_WALLET, ENDPOINT_WALLETS } from 'constants.js';
import { v4 as uuidv4 } from 'uuid';

// Multi-value filters go over the wire comma separated; an empty list sends no param (every value).
const serializeList = values => (values?.length > 0 ? values.join(',') : undefined);

// `orderBy` is 'amount Asc' / 'amount Desc'; `status` filters on pending/completed/rejected.
const read = async ({ pageNumber, pageSize, transactionType, status, orderBy } = {}) => {
  const authHeaders = await userService.getAuthorizationHeaders();
  const requestOptions = {
    method: 'GET',
    url: endpoint(
      `${ENDPOINT_WALLETS}/pas-account/${userService.getWalletPasAccountId()}`,
      false,
      API_URL_WALLET,
    ),
    headers: {
      ...authHeaders,
    },
    params: {
      pageNumber,
      pageSize,
      transactionType: serializeList(transactionType),
      status: serializeList(status),
      orderBy: orderBy || undefined,
    },
  };

  const response = httpWrapper(requestOptions);
  return response;
};

const create = async data => {
  const authHeaders = await userService.getAuthorizationHeaders();
  const requestOptions = {
    method: 'POST',
    url: endpoint(ENDPOINT_WALLETS, false, API_URL_WALLET),
    headers: {
      ...authHeaders,
      'X-Pbp-IdempotencyId': uuidv4(),
      'X-Pbp-ClientType': 'businessapp',
    },
    data: {
      corporateClientId: userService.getCorporateClientId(),
      ...data,
    },
  };

  const response = httpWrapper(requestOptions);
  return response;
};

const updateSettings = async ({ walletId, lowBalanceAlertThresholdMinor }) => {
  const authHeaders = await userService.getAuthorizationHeaders();
  const requestOptions = {
    method: 'PATCH',
    url: endpoint(
      `${ENDPOINT_WALLETS}/${userService.getCorporateClientId()}/${walletId}/settings`,
      false,
      API_URL_WALLET,
    ),
    headers: {
      ...authHeaders,
      'X-Pbp-ClientType': 'businessapp',
      'X-Pbp-IdempotencyId': uuidv4(),
    },
    data: { lowBalanceAlertThresholdMinor },
  };

  const response = httpWrapper(requestOptions);
  return response;
};

const wireLoad = async data => {
  const authHeaders = await userService.getAuthorizationHeaders();
  const requestOptions = {
    method: 'POST',
    url: endpoint(
      `${ENDPOINT_WALLETS}/${userService.getCorporateClientId()}/wire-loads`,
      false,
      API_URL_WALLET,
    ),
    headers: {
      ...authHeaders,
      'X-Pbp-ClientType': 'businessapp',
      'X-Pbp-IdempotencyId': uuidv4(),
    },
    data,
  };

  const response = httpWrapper(requestOptions);
  return response;
};

// Starts a card top-up. The 201 always comes back `processing` — any SCA challenge and the final
// credited/failed outcome only ever show up on the pollable operation (see readCardLoad). The
// caller supplies the workflow id so the create and every poll share one trace.
const createCardLoad = async ({ workflowId, ...data }) => {
  const authHeaders = await userService.getAuthorizationHeaders();
  const requestOptions = {
    method: 'POST',
    url: endpoint(
      `${ENDPOINT_WALLETS}/${userService.getCorporateClientId()}/card-load-operations`,
      false,
      API_URL_WALLET,
    ),
    headers: {
      ...authHeaders,
      'X-Pbp-ClientType': 'businessapp',
      'X-Pbp-IdempotencyId': uuidv4(),
      'X-Pbp-WorkflowId': workflowId,
    },
    // { walletId, amountMinor, paymentAccountId, userAccountId, clientBrowserDetails } — the
    // service rejects unknown members, so nothing else may be spread in here.
    data,
  };

  const response = httpWrapper(requestOptions);
  return response;
};

const readCardLoad = async ({ operationId, workflowId }) => {
  const authHeaders = await userService.getAuthorizationHeaders();
  const requestOptions = {
    method: 'GET',
    url: endpoint(
      `${ENDPOINT_WALLETS}/${userService.getCorporateClientId()}/card-load-operations/${operationId}`,
      false,
      API_URL_WALLET,
    ),
    headers: {
      ...authHeaders,
      'X-Pbp-ClientType': 'businessapp',
      'X-Pbp-WorkflowId': workflowId,
    },
  };

  const response = httpWrapper(requestOptions);
  return response;
};

// This PUTs straight to the wire-loads response's presigned S3 URL, not our own backend — it
// deliberately skips httpWrapper, which would add Authorization/X-Pbp-WorkflowId headers that
// aren't part of the URL's signature and would break the upload (S3 only allows the exact
// headers it signed for, and its CORS config doesn't allow our auth headers anyway).
const uploadWireLoadProof = ({ uploadUrl, headers, file }) =>
  axios.put(uploadUrl, file, { headers });

// Mocked until the wallet-service freeze/close endpoints exist (FS-184 / FS-186). The shape mirrors
// what the dashboard expects from GET wallet afterwards: the new `status` and the moment it changed
// (`statusChangedAt`, still to be added to the wallet-service response — see FS-170).
const freeze = async () =>
  Promise.resolve({ data: { status: 'frozen', statusChangedAt: new Date().toISOString() } });

const close = async note =>
  Promise.resolve({ data: { status: 'closed', statusChangedAt: new Date().toISOString(), note } });

const wallet = {
  read,
  create,
  updateSettings,
  wireLoad,
  uploadWireLoadProof,
  createCardLoad,
  readCardLoad,
  freeze,
  close,
};

export default wallet;
